import { createApi } from '@reduxjs/toolkit/query/react';
import { AxiosProgressEvent } from 'axios';

import { apiSahClaims } from '~constants/api';
import { VALIDATION_TIMEOUT_MS } from '~constants/common';

import { Paginated, Request, rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

// TAV-2596. Cross-slice invalidation: reconciliationsApi is a SEPARATE createApi with its own
// reducerPath, so a tag listed in this slice's invalidatesTags can never reach it.
import { reconciliationsApi } from '~support-at-home/actions/reconciliations';

import {
  Claim,
  ClaimableResponse,
  ClaimApiPushResult,
  ClaimFinaliseResponse,
  ClaimFinaliseSubmitResponse,
  ClaimItem,
  ClaimItemAttachment,
  ClaimStatusRefreshResult,
  ClaimSubmitResponse,
  DeleteClaimItemAttachmentResponse,
  SubmitClaimsResponse,
  UploadClaimItemAttachmentResponse,
} from '~support-at-home/models';

// Track validation timeouts per claim
const validationTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

export const typePrefix = 'claims';

// TODO: Strict Req / Res types
interface ClaimCreateResponse {
  claim: Claim;
}

export const claimsApi = createApi({
  reducerPath: 'claimsApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahClaims }),
  tagTypes: ['claims', 'claims-estimates', 'claim-items', 'claim-item-attachments'],
  endpoints: (builder) => ({
    getClaims: builder.query<Paginated<Claim>, Request | void>({
      query: (params) => ({ url: '', method: 'GET', params }),
      providesTags: [{ type: 'claims', id: 'LIST' }],
      transformResponse: (payload) => unwrap<Paginated<Claim>>(payload),
    }),
    getClaim: builder.query<Claim, Request & { id: string }>({
      query: ({ id, ...rest }) => ({ url: `/${id}`, method: 'GET', params: rest }),
      providesTags: (_result, _error, { id }) => [{ type: 'claims', id }],
      transformResponse: (payload) => unwrap<Claim>(payload),
    }),
    getClaimsEstimate: builder.query<any, Request>({
      query: (params) => ({ url: '/estimate', method: 'GET', params }),
      providesTags: [{ type: 'claims-estimates', id: 'LIST' }],
      transformResponse: (payload) => unwrap<any>(payload),
    }),
    createClaim: builder.mutation<ClaimCreateResponse, Request>({
      query: (body) => ({ url: '', method: 'POST', body }),
      invalidatesTags: [
        { type: 'claims', id: 'LIST' },
        { type: 'claims-estimates', id: 'LIST' },
      ],
      transformResponse: (payload) => unwrap<ClaimCreateResponse>(payload),
    }),
    deleteClaim: builder.mutation<any, Request & { id: string }>({
      query: ({ id }) => ({ url: `/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error) => [{ type: 'claims' }],
      transformResponse: (payload) => unwrap<any>(payload),
    }),
    // Response shape documented on ClaimFinaliseResponse (TAV-2590). `claim-items` must be
    // invalidated here (not just `claims`) - otherwise the per-item `sa_status` column (which reads
    // sa_invoice_item_id/sa_attachment_required/etc off claim ITEMS, a separate cached query)
    // never refreshes after a push, even though the claim-level counts do.
    // `id: 'LIST'` too, matching every other status-changing mutation here (create/validate/cancel):
    // `getClaims` provides ONLY the LIST tag and the list has no mount-or-focus refetch, so without
    // it a finalise done from the detail page leaves the list's `status` column stale for the
    // remainder of `keepUnusedDataFor` when the user navigates back.
    finaliseClaim: builder.mutation<ClaimFinaliseResponse, Request & { id: string }>({
      query: ({ id, ...rest }) => ({ url: `/${id}/finalise`, method: 'POST', body: rest }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'claims', id: 'LIST' },
        { type: 'claims', id },
        { type: 'claim-items', id: `CLAIM_${id}` },
      ],
      transformResponse: (payload) => unwrap<ClaimFinaliseResponse>(payload),
    }),
    // Unfinalise no longer clears the per-item SA columns - the invoice stays linked - but
    // `claim-items` still has to refetch, because re-opening a SUBMITTED claim moves the invoice back
    // to OPEN at Services Australia and refreshes the per-item state the `sa_status` column renders.
    //
    // `CLAIMABLE` is here because this mutation has ABSORBED Re-open: unfinalising a submitted claim
    // takes it back out of the submitted/unclaimed pool, so without this the cached claimable list
    // would keep offering it to the list page's Submit Claims button for the rest of keepUnusedDataFor.
    unfinaliseClaim: builder.mutation<any, Request & { id: string }>({
      query: ({ id, ...rest }) => ({ url: `/${id}/unfinalise`, method: 'POST', body: rest }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'claims', id: 'LIST' },
        { type: 'claims', id: 'CLAIMABLE' },
        { type: 'claims', id },
        { type: 'claim-items', id: `CLAIM_${id}` },
      ],
      transformResponse: (payload) => unwrap<any>(payload),
    }),
    // Draft-side push, and the re-send. Same three-tag invalidation as finaliseClaim and for the same
    // reasons - but note it does NOT invalidate CLAIMABLE: sending only opens the invoice at Services
    // Australia, and `getClaimableClaims` selects on the claim being `submitted`, so nothing about the
    // claimable pool can have changed.
    //
    // `claim-items` matters more here than anywhere else in this file: a reconcile can create, update
    // AND delete SA items in one call, so the per-item `sa_status` column is stale for a larger set of
    // rows than a plain push ever left behind.
    sendClaimToSa: builder.mutation<ClaimApiPushResult, Request & { id: string }>({
      query: ({ id, ...rest }) => ({ url: `/${id}/send-to-sa`, method: 'POST', body: rest }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'claims', id: 'LIST' },
        { type: 'claims', id },
        { type: 'claim-items', id: `CLAIM_${id}` },
      ],
      transformResponse: (payload) => unwrap<ClaimApiPushResult>(payload),
    }),
    // Lodges the claim. Invalidates CLAIMABLE for the same reason submitClaim does - this is the
    // transition that makes a claim eligible for `getClaimableClaims`, and that query provides ONLY
    // the CLAIMABLE tag, so without it the list's Submit Claims button stays disabled for the rest of
    // keepUnusedDataFor.
    finaliseAndSubmitClaim: builder.mutation<ClaimFinaliseSubmitResponse, Request & { id: string }>({
      query: ({ id, ...rest }) => ({ url: `/${id}/finalise-submit`, method: 'POST', body: rest }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'claims', id: 'LIST' },
        { type: 'claims', id: 'CLAIMABLE' },
        { type: 'claims', id },
        { type: 'claim-items', id: `CLAIM_${id}` },
      ],
      transformResponse: (payload) => unwrap<ClaimFinaliseSubmitResponse>(payload),
    }),
    // Same three-tag invalidation as finaliseClaim: submit refreshes every item's SA requirement
    // flags server-side (refreshApiInvoiceState), so `claim-items` must refetch, and the claim's
    // status changes, which the LIST renders.
    // `CLAIMABLE` too (TAV-2594): submitting the invoice is the exact transition that makes a claim
    // eligible for `getClaimableClaims`, and that query provides ONLY the CLAIMABLE tag - so without
    // it, submitting from the detail page then returning to the list leaves the Submit Claims button
    // disabled (and the confirmation preview missing the claim) for the rest of keepUnusedDataFor.
    submitClaim: builder.mutation<ClaimSubmitResponse, Request & { id: string }>({
      query: ({ id, ...rest }) => ({ url: `/${id}/submit`, method: 'POST', body: rest }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'claims', id: 'LIST' },
        { type: 'claims', id: 'CLAIMABLE' },
        { type: 'claims', id },
        { type: 'claim-items', id: `CLAIM_${id}` },
      ],
      transformResponse: (payload) => unwrap<ClaimSubmitResponse>(payload),
    }),
    // Drives the claims-list Submit Claims button (sa_connected -> renders at all;
    // claims.length -> enabled) and the confirmation modal's contents, in one call.
    getClaimableClaims: builder.query<ClaimableResponse, void>({
      query: () => ({ url: '/claimable', method: 'GET' }),
      providesTags: [{ type: 'claims', id: 'CLAIMABLE' }],
      transformResponse: (payload) => unwrap<ClaimableResponse>(payload),
    }),
    // Company-scoped: no claim id. Lodges ONE Services Australia claim covering every submitted,
    // unclaimed invoice. Distinct from submitClaim above, which submits a single INVOICE.
    // Invalidates the whole LIST because it can change the status of many claims at once.
    submitClaims: builder.mutation<SubmitClaimsResponse, void>({
      query: () => ({ url: '/submit-claim', method: 'POST' }),
      invalidatesTags: [{ type: 'claims', id: 'LIST' }, { type: 'claims', id: 'CLAIMABLE' }],
      transformResponse: (payload) => unwrap<SubmitClaimsResponse>(payload),
    }),
    // TAV-2595. Lazy, on-access refresh of Services Australia claim + invoice statuses, throttled
    // server-side (no request body - the throttle window is a server config value). A skipped
    // sweep (`checked: false`) is still a 200: nothing went wrong, the data is simply fresh, not
    // connected, or there is nothing to sweep yet.
    //
    // `invalidatesTags` MUST stay the function form. The static form would refetch the LIST on
    // every single page open, because a skipped sweep is a 200 - only a sweep that actually ran can
    // have changed a claim's status.
    //
    // TAV-2596. The sweep now also pulls payment statements and can create reconciliations, so BOTH
    // lists can be stale once it resolves. Claims are invalidated below; reconciliations cannot be,
    // because they live in a different createApi slice (reducerPath 'reconciliationsApi') and RTK
    // Query tags do not cross slices - listing a `reconciliations` tag here would silently no-op.
    // onQueryStarted dispatches into that slice explicitly instead.
    refreshClaimStatuses: builder.mutation<ClaimStatusRefreshResult, void>({
      query: () => ({ url: '/refresh-status', method: 'POST' }),
      invalidatesTags: (result) => (result?.checked ? [{ type: 'claims', id: 'LIST' }] : []),
      transformResponse: (payload) => unwrap<ClaimStatusRefreshResult>(payload),
      // Gated on reconciliations actually having been CREATED, for the same reason the claims
      // invalidation is gated on `checked`: a sweep that swept but reconciled nothing has not
      // changed the reconciliation list, and refetching it on every tab activation would be waste.
      // Narrowing on `checked` first is required - the skipped branch of the union has no counts.
      //
      // `?? 0` because the counter is OPTIONAL on the model: the web can run against an API that
      // predates this field (the API half ships on its own branch and its own pipeline). An older
      // API therefore reads as zero created and simply never triggers the refetch. The coalesce is
      // what makes that explicit - `undefined > 0` is already false, but the guard should not rely
      // on a reader knowing that, and it must survive being rewritten into a form where `undefined`
      // is not harmless.
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data?.checked && (data.reconciliations_created ?? 0) > 0) {
            dispatch(reconciliationsApi.util.invalidateTags([{ type: 'reconciliations', id: 'LIST' }]));
          }
        } catch {
          // A failed sweep created nothing, so there is nothing to invalidate. The error itself is
          // surfaced by the caller's own .catch (Claims.tsx shows the vendor toast) - swallowing it
          // here must not, and does not, suppress that.
        }
      },
    }),
    cancelValidation: builder.mutation<any, Request & { id: string }>({
      query: ({ id }) => ({ url: `/${id}/cancel-validation`, method: 'POST' }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'claims', id: 'LIST' },
        { type: 'claims', id },
      ],
      transformResponse: (payload) => unwrap<any>(payload),
    }),
    validateClaim: builder.mutation<any, Request & { id: string }>({
      query: ({ id, ...rest }) => ({ url: `/${id}/validate`, method: 'POST', body: rest }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'claims', id: 'LIST' },
        { type: 'claims', id },
      ],
      transformResponse: (payload) => unwrap<any>(payload),
      async onQueryStarted({ id }, { dispatch, getState, queryFulfilled }) {
        // Clear any existing timeout for this claim (if re-validating)
        const existingTimeout = validationTimeouts.get(String(id));
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        // Optimistically update individual claim query for detail view
        const patchClaim = dispatch(
          claimsApi.util.updateQueryData('getClaim', { id }, (draft) => {
            draft.is_validating = true;
          }),
        );

        // Optimistically update claim list query for snappy UX response
        const listPatches = claimsApi.util
          .selectInvalidatedBy(getState(), [{ type: 'claims', id: 'LIST' }])
          .filter(({ endpointName }) => endpointName === 'getClaims')
          .map(({ originalArgs }) =>
            dispatch(
              claimsApi.util.updateQueryData('getClaims', originalArgs, (draft) => {
                const claim = draft.rows.find((row) => String(row.id) === String(id));
                if (claim) {
                  claim.is_validating = true;
                }
              }),
            ),
          );

        // Hard timeout to abort validation state and stop polling after 3 minutes
        const timeoutId = setTimeout(() => {
          validationTimeouts.delete(String(id));

          // call cancel validation endpoint internally
          dispatch(claimsApi.endpoints.cancelValidation.initiate({ id }));
        }, VALIDATION_TIMEOUT_MS);

        validationTimeouts.set(String(id), timeoutId);

        try {
          await queryFulfilled;
        } catch {
          clearTimeout(timeoutId);
          validationTimeouts.delete(String(id));
          patchClaim.undo();
          listPatches.forEach((patch) => patch.undo());
        }
      },
    }),

    // claim item
    getClaimItems: builder.query<Paginated<ClaimItem>, Request & { id: string }>({
      query: ({ id, ...rest }) => ({ url: `/${id}/items`, method: 'GET', params: rest }),
      providesTags: (_result, _error, { id }) => [{ type: 'claim-items', id: `CLAIM_${id}` }],
      transformResponse: (payload) => unwrap<Paginated<ClaimItem>>(payload),
    }),
    getClaimItem: builder.query<ClaimItem, Request & { id: string }>({
      query: ({ id, ...rest }) => ({ url: `/items/${id}`, method: 'GET', params: rest }),
      providesTags: (_result, _error, { id }) => [{ type: 'claim-items', id }],
      transformResponse: (payload) => unwrap<ClaimItem>(payload),
    }),
    updateClaimItem: builder.mutation<ClaimItem, { id: string; claim_id: string; changes: Partial<ClaimItem> }>({
      query: ({ id, changes }) => ({
        url: `/items/${id}`,
        method: 'PATCH',
        body: changes,
      }),
      transformResponse: (payload) => unwrap<ClaimItem>(payload),
      invalidatesTags: (_result, _error, { claim_id }) => [
        { type: 'claim-items', id: `CLAIM_${claim_id}` },
      ],
    }),
    bulkUpdateClaimItems: builder.mutation<void, { ids: string[]; claim_id: string; changes: Partial<ClaimItem> }>({
      query: ({ ids, changes }) => ({
        url: `/items/bulk-update`,
        method: 'POST',
        body: { item_ids: ids, changes },
      }),
      invalidatesTags: (_result, _error, { claim_id }) => [
        { type: 'claims', id: claim_id },
        { type: 'claim-items', id: `CLAIM_${claim_id}` },
      ],
    }),
    deleteClaimItem: builder.mutation<void, { id: string; claim_id: string }>({
      query: ({ id }) => ({
        url: `/items/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { claim_id }) => [{ type: 'claim-items', id: `CLAIM_${claim_id}` }],
      transformResponse: (payload) => unwrap<void>(payload),
    }),
    bulkDeleteClaimItems: builder.mutation<void, { ids: string[]; claim_id: string }>({
      query: ({ ids }) => ({
        url: `/items/bulk-delete`,
        method: 'POST',
        body: { item_ids: ids },
      }),
      invalidatesTags: (_result, _error, { claim_id }) => [
        { type: 'claims', id: claim_id },
        { type: 'claim-items', id: `CLAIM_${claim_id}` },
      ],
    }),

    // Late submission reason capture (TAV-2592). Deliberately a dedicated mutation against its own
    // endpoint, not `updateClaimItem` - the generic PATCH is hard-blocked on a finalised claim
    // (`validateSingleItemId`), which is precisely the one state Services Australia raises
    // `requireLateSubmissionReason` in. `claimId` is stripped from the body inside `query` - the
    // API's two-key whitelist 400s on anything else.
    updateClaimItemLateSubmission: builder.mutation<
      ClaimItem,
      {
        id: string;
        claimId?: string;
        late_submission_reason_code: string | null;
        late_submission_justification?: string | null;
      }
    >({
      query: ({ id, claimId, ...body }) => ({
        url: `/items/${id}/late-submission`,
        method: 'PUT',
        body,
      }),
      // Both claim-level and item-level tags: saving a reason changes the row's indicator AND the
      // claim's items_late_submission_required count behind the banner.
      invalidatesTags: (_result, _error, { id, claimId }) => [
        { type: 'claim-items' as const, id },
        ...(claimId
          ? [
              { type: 'claims' as const, id: claimId },
              { type: 'claim-items' as const, id: `CLAIM_${claimId}` },
            ]
          : []),
      ],
      transformResponse: (payload) => unwrap<ClaimItem>(payload),
    }),

    // Services Australia claim-item attachments (TAV-2591). The list endpoint returns a bare array
    // under `data` (not a Paginated<T>) - the API uses Query.findAll with limit:null.
    getClaimItemAttachments: builder.query<ClaimItemAttachment[], { id: string }>({
      query: ({ id }) => ({ url: `/items/${id}/attachments`, method: 'GET' }),
      providesTags: (_result, _error, { id }) => [{ type: 'claim-item-attachments', id }],
      transformResponse: (payload) => unwrap<ClaimItemAttachment[]>(payload),
    }),
    uploadClaimItemAttachment: builder.mutation<
      UploadClaimItemAttachmentResponse,
      {
        id: string;
        claimId?: string;
        file: File;
        attachment_type: string;
        onProgress?: (percent: number) => void;
      }
    >({
      // FormData is built INSIDE query (mirrors analyseUploadedFile, supplierInvoices.ts:100-110).
      // Never set Content-Type - axios detects FormData and writes the multipart boundary itself.
      query: ({ id, file, attachment_type, onProgress }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('attachment_type', attachment_type);
        return {
          url: `/items/${id}/attachments`,
          method: 'POST',
          body: formData,
          onUploadProgress: (evt: AxiosProgressEvent) => {
            if (onProgress && evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100));
          },
        };
      },
      // Invalidating the attachment list alone is not enough. A successful upload changes the claim
      // item's evidence state server-side (the controller writes back SA's attachmentProvided), so
      // the `sa_status` cell and the claim-level "N items require attachments" banner both go stale
      // and would keep showing the warning until an unrelated refetch happened to run.
      // `claimId` is optional because the claim-items/claims tags are only knowable from the row -
      // when absent, only the attachment list refreshes (the pre-existing behaviour).
      invalidatesTags: (_result, _error, { id, claimId }) => [
        { type: 'claim-item-attachments' as const, id },
        ...(claimId
          ? [
              { type: 'claims' as const, id: claimId },
              { type: 'claim-items' as const, id: `CLAIM_${claimId}` },
            ]
          : []),
      ],
      transformResponse: (payload) => unwrap<UploadClaimItemAttachmentResponse>(payload),
    }),
    // `claimItemId` is carried purely to build the invalidation tag - the URL does not need it.
    deleteClaimItemAttachment: builder.mutation<
      DeleteClaimItemAttachmentResponse,
      { attachmentId: string; claimItemId: string; claimId?: string }
    >({
      query: ({ attachmentId }) => ({ url: `/items/attachments/${attachmentId}`, method: 'DELETE' }),
      // Mirrors uploadClaimItemAttachment: deleting the last attachment flips attachmentProvided
      // back to false server-side, so the warning must reappear on the row and in the banner.
      invalidatesTags: (_result, _error, { claimItemId, claimId }) => [
        { type: 'claim-item-attachments' as const, id: claimItemId },
        ...(claimId
          ? [
              { type: 'claims' as const, id: claimId },
              { type: 'claim-items' as const, id: `CLAIM_${claimId}` },
            ]
          : []),
      ],
      transformResponse: (payload) => unwrap<DeleteClaimItemAttachmentResponse>(payload),
    }),
  }),
});

export const {
  useGetClaimsQuery,
  useGetClaimQuery,
  useGetClaimsEstimateQuery,
  useCreateClaimMutation,
  useDeleteClaimMutation,
  useFinaliseClaimMutation,
  useUnfinaliseClaimMutation,
  useSendClaimToSaMutation,
  useFinaliseAndSubmitClaimMutation,
  useSubmitClaimMutation,
  useGetClaimableClaimsQuery,
  useSubmitClaimsMutation,
  useRefreshClaimStatusesMutation,
  useValidateClaimMutation,

  useGetClaimItemsQuery,
  useGetClaimItemQuery,
  useUpdateClaimItemMutation,
  useBulkUpdateClaimItemsMutation,
  useDeleteClaimItemMutation,
  useBulkDeleteClaimItemsMutation,
  useUpdateClaimItemLateSubmissionMutation,

  useGetClaimItemAttachmentsQuery,
  useUploadClaimItemAttachmentMutation,
  useDeleteClaimItemAttachmentMutation,
} = claimsApi;
